import React, { useState, useEffect } from 'react';
import { inventoryAPI, rawMaterialsAPI } from '../services/api';
import { MobileCard } from '../components/MobileCard';
import '../components/MobileCard.css';
import './Inventory.css';

interface RawMaterial {
  id: number;
  name: string;
  category_name: string;
  unit: string;
  cost_per_unit: number;
  supplier_name: string;
  reorder_level: number;
  reorder_quantity: number;
}

interface InventoryItem {
  id: number;
  raw_material: number;
  raw_material_name: string;
  quantity_on_hand: number;
  reserved_quantity: number;
  available_quantity: number;
  unit: string;
  unit_of_measure?: string;  // API may return this instead of unit
  cost_per_unit: number;
  reorder_level: number;
  last_updated: string;
}

// Phase 2: editable nutrient fields. Groups match Material # / the formula nutrition panel.
const NUTRIENT_GROUPS: { label: string; unit: string; step: string; fields: string[] }[] = [
  { label: 'Macro nutrients', unit: 'fraction (0.12 = 12%)', step: '0.000001',
    fields: ['protein', 'lysine', 'methionine', 'crude_fat', 'adf_fiber', 'ndf_fiber', 'calcium_min',
             'calcium_max', 'phosphorus', 'salt_min', 'salt_max', 'magnesium', 'sulfur', 'potassium'] },
  { label: 'Trace minerals', unit: 'PPM', step: '0.000001',
    fields: ['selenium', 'zinc', 'copper', 'iron', 'manganese', 'aluminum', 'niacin', 'choline'] },
  { label: 'Energy', unit: 'Kcal/lb', step: '0.000001', fields: ['energy_kcal_lb'] },
  { label: 'Vitamins', unit: 'IU/lb', step: '0.000001', fields: ['vitamin_a', 'vitamin_d3', 'vitamin_e'] },
  { label: 'Unit factor', unit: 'lb per purchase unit', step: '0.0001', fields: ['lb_per_unit'] },
];
const NUTRIENT_FIELDS: string[] = NUTRIENT_GROUPS.flatMap(g => g.fields);
const NUTRIENT_LABELS: Record<string, string> = {
  protein: 'Protein', lysine: 'Lysine', methionine: 'Methionine', crude_fat: 'Crude Fat', adf_fiber: 'ADF Fiber',
  ndf_fiber: 'NDF Fiber', calcium_min: 'Calcium min', calcium_max: 'Calcium max', phosphorus: 'Phosphorus',
  salt_min: 'Salt min', salt_max: 'Salt max', magnesium: 'Magnesium', sulfur: 'Sulfur', potassium: 'Potassium',
  selenium: 'Selenium', zinc: 'Zinc', copper: 'Copper', iron: 'Iron', manganese: 'Manganese', aluminum: 'Aluminum',
  niacin: 'Niacin', choline: 'Choline', energy_kcal_lb: 'Kcal/lb', vitamin_a: 'Vit A', vitamin_d3: 'Vit D3',
  vitamin_e: 'Vit E', lb_per_unit: 'lb/unit',
};

const Inventory: React.FC = () => {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [filteredInventory, setFilteredInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  // Phase 2: editable nutrients on the detail modal
  const [material, setMaterial] = useState<any | null>(null);
  const [nutrientEdits, setNutrientEdits] = useState<Record<string, string>>({});
  const [isSavingNutrients, setIsSavingNutrients] = useState(false);
  const [nutritionError, setNutritionError] = useState('');
  const [nutritionNotice, setNutritionNotice] = useState('');

  useEffect(() => {
    loadInventory();
  }, []);

  useEffect(() => {
    filterInventory();
  }, [inventory, searchTerm, showLowStockOnly]);

  const loadInventory = async () => {
    try {
      setLoading(true);
      const response = await inventoryAPI.getAll();
      const inventoryData = response.data.results || response.data;
      setInventory(inventoryData);
      setFilteredInventory(inventoryData);
    } catch (err) {
      console.error('Error loading inventory:', err);
      setError('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get the unit with fallback
  const getUnit = (item: InventoryItem) => item.unit || item.unit_of_measure || 'lbs';

  // Phase 2: when the detail modal opens, fetch the full RawMaterial (carries the nutrient fields)
  // and seed the editable inputs. Closing the modal resets the editor.
  useEffect(() => {
    if (!selectedItem) { setMaterial(null); setNutrientEdits({}); setNutritionError(''); setNutritionNotice(''); return; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await rawMaterialsAPI.getById(selectedItem.raw_material);
        if (cancelled) return;
        setMaterial(resp.data);
        const init: Record<string, string> = {};
        NUTRIENT_FIELDS.forEach(f => { const v = resp.data[f]; init[f] = (v == null ? '' : String(v)); });
        setNutrientEdits(init);
        setNutritionError(''); setNutritionNotice('');
      } catch (e) {
        if (!cancelled) setNutritionError('Failed to load material nutrients');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedItem]);

  const handleNutrientChange = (field: string, value: string) =>
    setNutrientEdits(prev => ({ ...prev, [field]: value }));

  const handleSaveNutrients = async () => {
    if (!material) return;
    setIsSavingNutrients(true); setNutritionError(''); setNutritionNotice('');
    // Build a PATCH payload of ONLY changed nutrient fields. Empty input -> null (clear), NOT 0.
    const payload: Record<string, any> = {};
    NUTRIENT_FIELDS.forEach(f => {
      const orig = material[f] == null ? '' : String(material[f]);
      const cur = (nutrientEdits[f] ?? '').trim();
      if (cur !== orig) payload[f] = cur === '' ? null : cur;
    });
    if (Object.keys(payload).length === 0) {
      setNutritionNotice('No changes to save'); setIsSavingNutrients(false); return;
    }
    try {
      await rawMaterialsAPI.partialUpdate(material.id, payload);   // name/cost/category/stock never sent
      const fresh = await rawMaterialsAPI.getById(material.id);    // refetch so the modal reflects saved values
      setMaterial(fresh.data);
      const init: Record<string, string> = {};
      NUTRIENT_FIELDS.forEach(f => { const v = fresh.data[f]; init[f] = (v == null ? '' : String(v)); });
      setNutrientEdits(init);
      setNutritionNotice(`Saved ${Object.keys(payload).length} field(s)`);
    } catch (e: any) {
      setNutritionError(e.response?.data?.detail || e.response?.data?.error || 'Failed to save nutrients');
    } finally {
      setIsSavingNutrients(false);
    }
  };

  const filterInventory = () => {
    let filtered = [...inventory];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.raw_material_name || '').toLowerCase().includes(term)
      );
    }

    // Filter by low stock
    if (showLowStockOnly) {
      filtered = filtered.filter((item) => (item.available_quantity ?? 0) < (item.reorder_level ?? 0));
    }

    setFilteredInventory(filtered);
  };

  const getStockStatus = (item: InventoryItem) => {
    const available = item.available_quantity ?? 0;
    const reorderLevel = item.reorder_level ?? 0;
    if (available === 0) {
      return { status: 'OUT OF STOCK', class: 'status-out' };
    } else if (available < reorderLevel) {
      return { status: 'LOW STOCK', class: 'status-low' };
    } else {
      return { status: 'OK', class: 'status-ok' };
    }
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined || isNaN(Number(num))) {
      return '0';
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(num));
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) {
      return '$0.0000';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(Number(amount));
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const lowStockCount = inventory.filter(
    (item) => (item.available_quantity ?? 0) < (item.reorder_level ?? 0)
  ).length;
  const outOfStockCount = inventory.filter((item) => (item.available_quantity ?? 0) === 0).length;

  if (loading) {
    return (
      <div className="inventory-page">
        <div className="loading">Loading inventory...</div>
      </div>
    );
  }

  return (
    <div className="inventory-page">
      <div className="page-header">
        <h1>Raw Materials Inventory</h1>
        <p className="page-subtitle">Red Bridge Farm Feed Co.</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Stock Status Summary */}
      <div className="stock-summary">
        <div className="summary-card">
          <div className="summary-label">Total Materials</div>
          <div className="summary-value">{inventory.length}</div>
        </div>
        <div className="summary-card warning">
          <div className="summary-label">Low Stock</div>
          <div className="summary-value">{lowStockCount}</div>
        </div>
        <div className="summary-card danger">
          <div className="summary-label">Out of Stock</div>
          <div className="summary-value">{outOfStockCount}</div>
        </div>
      </div>

      <div className="inventory-container">
        {/* Filters */}
        <div className="filters-section">
          <div className="filter-row">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search materials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <label className="checkbox-filter">
              <input
                type="checkbox"
                checked={showLowStockOnly}
                onChange={(e) => setShowLowStockOnly(e.target.checked)}
              />
              <span>Show Low Stock Only</span>
            </label>
            <div className="results-count">
              {filteredInventory.length} material{filteredInventory.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Inventory Table */}
        <div className="desktop-table-container">
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Material</th>
                <th>On Hand</th>
                <th>Reserved</th>
                <th>Available</th>
                <th>Unit</th>
                <th>Reorder Level</th>
                <th>Cost/Unit</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInventory.length === 0 ? (
                <tr>
                  <td colSpan={9} className="no-data">
                    No materials found
                  </td>
                </tr>
              ) : (
                filteredInventory.map((item) => {
                  const stockStatus = getStockStatus(item);
                  return (
                    <tr
                      key={item.id}
                      className={`inventory-row ${stockStatus.class}`}
                      onClick={() => setSelectedItem(item)}
                    >
                      <td className="material-name">{item.raw_material_name || 'Unknown'}</td>
                      <td className="qty-column">{formatNumber(item.quantity_on_hand ?? 0)}</td>
                      <td className="qty-column">{formatNumber(item.reserved_quantity ?? 0)}</td>
                      <td className="qty-column available">{formatNumber(item.available_quantity ?? 0)}</td>
                      <td>{getUnit(item)}</td>
                      <td className="qty-column">{formatNumber(item.reorder_level ?? 0)}</td>
                      <td className="cost-column">{item.cost_per_unit ? formatCurrency(item.cost_per_unit) : '$0.00'}</td>
                      <td>
                        <span className={`stock-badge ${stockStatus.class}`}>{stockStatus.status}</span>
                      </td>
                      <td>
                        <button
                          className="btn-view"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem(item);
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards (shown on small screens) */}
        <div className="mobile-cards-container">
          <div className="mobile-card-list">
            {filteredInventory.length === 0 ? (
              <div className="no-data-card">No materials found</div>
            ) : (
              filteredInventory.map((item) => {
                const stockStatus = getStockStatus(item);
                return (
                  <MobileCard
                    key={item.id}
                    title={item.raw_material_name}
                    badge={
                      <span className={`stock-badge ${stockStatus.class}`}>
                        {stockStatus.status}
                      </span>
                    }
                    fields={[
                      { label: 'On Hand', value: `${formatNumber(item.quantity_on_hand ?? 0)} ${getUnit(item)}` },
                      { label: 'Available', value: `${formatNumber(item.available_quantity ?? 0)} ${getUnit(item)}` },
                      { label: 'Reorder Level', value: `${formatNumber(item.reorder_level ?? 0)} ${getUnit(item)}` },
                      { label: 'Cost/Unit', value: item.cost_per_unit ? formatCurrency(item.cost_per_unit) : '$0.00' },
                    ]}
                    actions={
                      <button className="btn btn-primary btn-sm" onClick={() => setSelectedItem(item)}>
                        View Details
                      </button>
                    }
                    onClick={() => setSelectedItem(item)}
                    className={stockStatus.class}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Inventory Detail Modal */}
      {selectedItem && (
        <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{selectedItem.raw_material_name}</h2>
                <span className={`stock-badge ${getStockStatus(selectedItem).class}`}>
                  {getStockStatus(selectedItem).status}
                </span>
              </div>
              <button className="modal-close" onClick={() => setSelectedItem(null)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Stock Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Quantity on Hand:</label>
                    <span className="qty-value">
                      {formatNumber(selectedItem.quantity_on_hand ?? 0)} {getUnit(selectedItem)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Reserved Quantity:</label>
                    <span className="qty-value">
                      {formatNumber(selectedItem.reserved_quantity ?? 0)} {getUnit(selectedItem)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Available Quantity:</label>
                    <span className="qty-value highlight">
                      {formatNumber(selectedItem.available_quantity ?? 0)} {getUnit(selectedItem)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Reorder Level:</label>
                    <span className="qty-value">
                      {formatNumber(selectedItem.reorder_level ?? 0)} {getUnit(selectedItem)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Cost per Unit:</label>
                    <span className="cost-value">{selectedItem.cost_per_unit ? formatCurrency(selectedItem.cost_per_unit) : '$0.00'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Last Updated:</label>
                    <span>{formatDate(selectedItem.last_updated)}</span>
                  </div>
                </div>
              </div>

              {(selectedItem.available_quantity ?? 0) < (selectedItem.reorder_level ?? 0) && (
                <div className="alert-box warning">
                  <strong>Reorder Recommended</strong>
                  <p>
                    Stock level is below the reorder threshold of {formatNumber(selectedItem.reorder_level ?? 0)}{' '}
                    {getUnit(selectedItem)}
                  </p>
                </div>
              )}

              {selectedItem.available_quantity === 0 && (
                <div className="alert-box danger">
                  <strong>✕ Out of Stock</strong>
                  <p>This material is currently out of stock and needs immediate attention.</p>
                </div>
              )}

              {/* Phase 2: editable nutrition. Fill gaps for blank materials. Excel-backed fields are
                  marked — editing those is transient (a future refresh overwrites). name/cost/stock untouched. */}
              {material && (
                <div className="detail-section">
                  <h3>Nutrition</h3>
                  {nutritionError && <div className="alert-box danger" style={{ marginBottom: 8 }}>{nutritionError}</div>}
                  {nutritionNotice && (
                    <div className="alert-box" style={{ marginBottom: 8, background: '#d1fae5', cursor: 'pointer' }} onClick={() => setNutritionNotice('')}>
                      {nutritionNotice}
                    </div>
                  )}
                  {NUTRIENT_GROUPS.map(group => (
                    <div key={group.label} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>
                        {group.label} <span style={{ opacity: 0.7 }}>({group.unit})</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 6 }}>
                        {group.fields.map(f => {
                          const fromExcel = material[f] != null;
                          return (
                            <label key={f} style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <span>
                                {NUTRIENT_LABELS[f]}
                                {fromExcel
                                  ? <span title="Has a value from Excel/refresh — edit it in the spreadsheet; an in-app edit here is transient (the next refresh overwrites)." style={{ color: '#b45309', marginLeft: 4 }}>· from Excel</span>
                                  : <span title="Blank — your gap to fill. A value entered here survives future refreshes (refresh skips blank Excel cells)." style={{ color: '#2563eb', marginLeft: 4 }}>· gap</span>}
                              </span>
                              <input type="number" step={group.step} value={nutrientEdits[f] ?? ''}
                                placeholder={fromExcel ? '' : 'enter…'}
                                onChange={(e) => handleNutrientChange(f, e.target.value)}
                                style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4 }} />
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <button className="btn btn-primary btn-sm" onClick={handleSaveNutrients} disabled={isSavingNutrients} style={{ marginTop: 6 }}>
                    {isSavingNutrients ? 'Saving…' : 'Save Nutrients'}
                  </button>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedItem(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
